import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Factory, 
  TrendingUp, 
  TrendingDown, 
  Package, 
  Boxes, 
  Target, 
  Calendar,
  RefreshCw,
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  DollarSign,
  BarChart3
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// Formatar moeda
const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// Formatar número grande (ex: 48k)
const formatarNumeroGrande = (valor) => {
  if (valor >= 1000000) {
    return `${(valor / 1000000).toFixed(1)}M`;
  }
  if (valor >= 1000) {
    return `${(valor / 1000).toFixed(1)}k`;
  }
  return valor.toFixed(0);
};

export default function Producao() {
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('semana');
  const [resumo, setResumo] = useState(null);
  const [dadosSemanais, setDadosSemanais] = useState([]);
  const [metaDialogOpen, setMetaDialogOpen] = useState(false);
  const [metaForm, setMetaForm] = useState({
    valor_semanal: '',
    valor_mensal: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [resumoRes, semanaisRes] = await Promise.all([
        axios.get(`${API}/producao/resumo?periodo=${periodo}`, { headers }),
        axios.get(`${API}/producao/semanal?semanas=8`, { headers })
      ]);

      setResumo(resumoRes.data);
      setDadosSemanais(semanaisRes.data.semanas || []);
    } catch (error) {
      console.error('Erro ao carregar dados de produção:', error);
      toast.error('Erro ao carregar dados de produção');
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveMeta = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/producao/meta`, {
        valor_semanal: parseFloat(metaForm.valor_semanal) || 50000,
        valor_mensal: parseFloat(metaForm.valor_mensal) || 200000
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meta atualizada com sucesso!');
      setMetaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar meta');
    }
  };

  // Preparar dados para o gráfico
  const dadosGrafico = dadosSemanais.map(s => ({
    name: s.semana,
    periodo: `${s.inicio} - ${s.fim}`,
    valor: s.valor,
    m3: s.volume_m3,
    produtos: s.qtd_produtos
  }));

  // Custom tooltip para o gráfico
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-3">
          <p className="font-semibold text-sm">{label}</p>
          <p className="text-xs text-muted-foreground">{payload[0]?.payload?.periodo}</p>
          <p className="text-primary font-bold mt-1">
            {formatarMoeda(payload[0]?.value)}
          </p>
          <p className="text-xs text-muted-foreground">
            {payload[0]?.payload?.produtos} produtos | {payload[0]?.payload?.m3?.toFixed(4)} m³
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando produção...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const metricas = resumo?.metricas || {};
  const comparativo = resumo?.comparativo || {};
  const meta = resumo?.meta || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Factory className="h-7 w-7 text-orange-500" />
              Produção
            </h1>
            <p className="text-muted-foreground">Acompanhe a produtividade da fábrica</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={periodo} onValueChange={setPeriodo}>
              <SelectTrigger className="w-[140px]" data-testid="periodo-select">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hoje">Hoje</SelectItem>
                <SelectItem value="semana">Esta Semana</SelectItem>
                <SelectItem value="mes">Este Mês</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
            <Dialog open={metaDialogOpen} onOpenChange={setMetaDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => {
                  setMetaForm({
                    valor_semanal: meta.valor_semanal?.toString() || '50000',
                    valor_mensal: meta.valor_mensal?.toString() || '200000'
                  });
                }}>
                  <Settings className="h-4 w-4 mr-2" />
                  Metas
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-orange-500" />
                    Configurar Metas de Produção
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Meta Semanal (R$)</Label>
                    <Input
                      type="number"
                      placeholder="50000"
                      value={metaForm.valor_semanal}
                      onChange={(e) => setMetaForm({ ...metaForm, valor_semanal: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Meta Mensal (R$)</Label>
                    <Input
                      type="number"
                      placeholder="200000"
                      value={metaForm.valor_mensal}
                      onChange={(e) => setMetaForm({ ...metaForm, valor_mensal: e.target.value })}
                    />
                  </div>
                  <Button onClick={handleSaveMeta} className="w-full">
                    Salvar Metas
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Cards de Métricas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Card 1 - Produção da Semana */}
          <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-orange-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Produção {periodo === 'hoje' ? 'Hoje' : periodo === 'semana' ? 'da Semana' : 'do Mês'}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {formatarMoeda(metricas.valor_total || 0)}
                  </p>
                  {/* Comparativo */}
                  <div className="flex items-center gap-1 mt-1">
                    {comparativo.tendencia === 'alta' ? (
                      <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30 text-xs">
                        <ArrowUpRight className="h-3 w-3 mr-1" />
                        +{comparativo.variacao_percentual}%
                      </Badge>
                    ) : comparativo.tendencia === 'baixa' ? (
                      <Badge className="bg-red-500/20 text-red-600 border-red-500/30 text-xs">
                        <ArrowDownRight className="h-3 w-3 mr-1" />
                        {comparativo.variacao_percentual}%
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-500/20 text-slate-600 border-slate-500/30 text-xs">
                        <Minus className="h-3 w-3 mr-1" />
                        0%
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">vs anterior</span>
                  </div>
                </div>
                <DollarSign className="h-10 w-10 text-orange-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Card 2 - m³ Produzidos */}
          <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">m³ Produzidos</p>
                  <p className="text-2xl sm:text-3xl font-bold text-amber-600">
                    {(metricas.volume_m3 || 0).toFixed(4)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">metros cúbicos</p>
                </div>
                <Boxes className="h-10 w-10 text-amber-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Card 3 - Produtos Finalizados */}
          <Card className="border-blue-500/30 bg-gradient-to-br from-blue-500/10 to-blue-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Produtos Finalizados</p>
                  <p className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {metricas.qtd_produtos || 0}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{metricas.qtd_pedidos || 0} pedidos</p>
                </div>
                <Package className="h-10 w-10 text-blue-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Card 4 - Ticket Médio */}
          <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ticket Médio</p>
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">
                    {formatarMoeda(metricas.ticket_medio || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">por produto</p>
                </div>
                <TrendingUp className="h-10 w-10 text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>

          {/* Card 5 - Meta de Produção */}
          <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-purple-500/5">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Meta {periodo === 'mes' ? 'Mensal' : 'Semanal'}</p>
                  <Target className="h-5 w-5 text-purple-500/50" />
                </div>
                <p className="text-lg font-bold text-purple-600">
                  {formatarMoeda(periodo === 'mes' ? meta.valor_mensal : meta.valor_semanal)}
                </p>
                <Progress 
                  value={Math.min(periodo === 'mes' ? meta.percentual_mensal : meta.percentual_semanal, 100)} 
                  className="h-2"
                />
                <p className="text-xs text-right text-muted-foreground">
                  {periodo === 'mes' ? meta.percentual_mensal : meta.percentual_semanal}% atingido
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico Principal */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Produção Semanal
            </CardTitle>
            <CardDescription>Evolução da produção nas últimas 8 semanas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dadosGrafico} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="name" 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    className="text-xs fill-muted-foreground"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `${formatarNumeroGrande(value)}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="valor" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorValor)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tabela de Produção Semanal */}
        <Card>
          <CardHeader>
            <CardTitle>Detalhamento por Semana</CardTitle>
            <CardDescription>Resumo detalhado da produção semanal</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Semana</TableHead>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-right">Produção (R$)</TableHead>
                    <TableHead className="text-right">m³</TableHead>
                    <TableHead className="text-right">Produtos</TableHead>
                    <TableHead className="text-right">Pedidos</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dadosSemanais.map((semana, idx) => (
                    <TableRow key={idx} className={idx === dadosSemanais.length - 1 ? 'bg-orange-500/5' : ''}>
                      <TableCell className="font-medium">
                        {semana.semana}
                        {idx === dadosSemanais.length - 1 && (
                          <Badge className="ml-2 bg-orange-500/20 text-orange-600 border-orange-500/30 text-xs">
                            Atual
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {semana.inicio} - {semana.fim}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-orange-600">
                        {formatarMoeda(semana.valor)}
                      </TableCell>
                      <TableCell className="text-right">
                        {semana.volume_m3.toFixed(4)}
                      </TableCell>
                      <TableCell className="text-right">
                        {semana.qtd_produtos}
                      </TableCell>
                      <TableCell className="text-right">
                        {semana.qtd_pedidos}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Lista de Pedidos Produzidos */}
        {resumo?.pedidos && resumo.pedidos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Pedidos Finalizados no Período</CardTitle>
              <CardDescription>{resumo.pedidos.length} pedidos produzidos</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {resumo.pedidos.slice(0, 20).map((pedido, idx) => (
                  <div 
                    key={pedido.id || idx}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                        <Package className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {pedido.numero_pedido || 'Pedido'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {pedido.cliente_nome || 'Cliente não informado'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-orange-600">
                        {formatarMoeda(pedido.total_venda || pedido.valor_total || pedido.total_com_desconto || 0)}
                      </p>
                      <Badge variant="outline" className="text-xs">
                        {pedido.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
